/* ==========================================================================
   GEOTECH · ARC — Scroll motion engine
   One IntersectionObserver drives every reveal. Only opacity/transform change,
   so the browser composites on the GPU and scrolling stays at 60fps. A single
   clamped, rAF-throttled scroll handler powers the hero parallax + header
   state. Fully inert under prefers-reduced-motion.
   ========================================================================== */
(() => {
  'use strict';

  if (window.__geoScrollMotion) return;
  window.__geoScrollMotion = true;

  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Respect reduced motion completely — leave everything visible, do nothing.
  if (reduceMotion.matches) {
    root.classList.remove('geo-motion');
    root.classList.add('geo-motion-ready');
    return;
  }

  // Progressive fallback: an older browser must never keep content hidden.
  if (!('IntersectionObserver' in window)) {
    root.classList.remove('geo-motion');
    root.classList.add('geo-motion-ready', 'geo-motion-failsafe');
    return;
  }

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const STAGGER_CAP = 9; // caps cascade delay so late items never lag too long

  /* --------------------------------------------------------------------------
     Reveal specs
     lead  : single element per section, revealed as one block
     group : repeating children within a root, revealed with a cascade
     -------------------------------------------------------------------------- */
  const LEAD = [
    { sel: '.gpg__header', variant: '' },
    { sel: '.gtr-top', variant: '' },
    { sel: '.gjr__hero', variant: '' },
    { sel: '.gjr__summary', variant: 'geo-reveal--fast' },
    { sel: '.gea__header', variant: '' },
    { sel: '.gcl__header', variant: '' },
    { sel: '.gfd__media', variant: 'geo-reveal--left' },
    { sel: '.gfd__info', variant: 'geo-reveal--right' },
    { sel: '.gea__visual', variant: 'geo-reveal--right' },
  ];

  const GROUPS = [
    { root: '.gpg', item: '.gpg-card', variant: '' },
    { root: '.gtr', item: '.gtr-photo', variant: 'geo-reveal--fast' },
    { root: '.gtr', item: '.gtr-card', variant: 'geo-reveal--fast' },
    { root: '.gea', item: '.gea__item', variant: 'geo-reveal--fast' },
    { root: '.gcl', item: '.gcl-card', variant: '' },
  ];

  let observer = null;

  const settle = (el) => {
    const done = () => {
      el.classList.add('geo-motion-done');
      el.removeEventListener('transitionend', done);
    };
    el.addEventListener('transitionend', done);
  };

  const reveal = (el) => {
    el.classList.add('is-in');
    settle(el);
  };

  const tag = (el, variant, order) => {
    if (!el || el.dataset.geoReveal === '1') return;
    el.dataset.geoReveal = '1';
    el.classList.add('geo-reveal');
    if (variant) el.classList.add(...variant.split(' ').filter(Boolean));
    if (order != null) el.style.setProperty('--geo-order', String(order));
    if (observer) observer.observe(el);
  };

  const scan = (scope = document) => {
    LEAD.forEach(({ sel, variant }) => {
      scope.querySelectorAll(sel).forEach((el) => tag(el, variant, 0));
    });
    GROUPS.forEach(({ root: rootSel, item, variant }) => {
      scope.querySelectorAll(rootSel).forEach((container) => {
        const items = container.querySelectorAll(item);
        items.forEach((el, i) => tag(el, variant, clamp(i, 0, STAGGER_CAP)));
      });
    });
  };

  /* --------------------------------------------------------------------------
     Hero intro — above the fold, so it plays on load rather than on scroll.
     -------------------------------------------------------------------------- */
  const initHeroes = () => {
    document.querySelectorAll('.geo-hero').forEach((hero) => {
      const kids = hero.querySelectorAll('.geo-hero__content > *');
      kids.forEach((el, i) => el.style.setProperty('--geo-order', String(i)));
      const bar = hero.querySelector('.geo-hero__statbar');
      if (bar) bar.style.setProperty('--geo-order', String(kids.length));
      // Two rAFs: guarantees the hidden from-state is painted before we animate.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => hero.classList.add('geo-hero-in'))
      );
    });
  };

  /* --------------------------------------------------------------------------
     Scroll-linked work: header state + hero parallax. One handler, rAF-gated.
     -------------------------------------------------------------------------- */
  const finePointer = window.matchMedia('(pointer: fine)').matches;
  const parallaxOn = finePointer;
  if (parallaxOn) root.classList.add('geo-parallax-on');

  let heroMedia = null;
  let heroEl = null;
  let ticking = false;

  const onScrollFrame = () => {
    ticking = false;
    const y = window.pageYOffset || document.documentElement.scrollTop || 0;

    // Header condense/elevation past a small threshold.
    if (y > 20) root.classList.add('geo-scrolled');
    else root.classList.remove('geo-scrolled');

    // Hero parallax — only while the hero is on screen, clamped within the
    // media's scale headroom so edges never show.
    if (parallaxOn && heroMedia && heroEl) {
      const rect = heroEl.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        const p = clamp(-rect.top * 0.12, -16, 16);
        heroMedia.style.setProperty('--geo-parallax', p.toFixed(1) + 'px');
      }
    }
  };

  const requestScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(onScrollFrame);
  };

  /* --------------------------------------------------------------------------
     Header offset for smooth anchor jumps (scroll-padding-top).
     -------------------------------------------------------------------------- */
  const setHeaderOffset = () => {
    const header =
      document.querySelector('.geo-arc-header') ||
      document.querySelector('.section-header .header') ||
      document.querySelector('.shopify-section-group-header-group .header');
    if (header) {
      const h = Math.round(header.getBoundingClientRect().height);
      if (h > 0) root.style.setProperty('--geo-scroll-offset', h + 24 + 'px');
    }
  };

  /* --------------------------------------------------------------------------
     Boot
     -------------------------------------------------------------------------- */
  const init = () => {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      // Trigger a touch before fully in view so sections greet the scroll.
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    // The engine is now fully initialized; the head bootstrap can stand down.
    root.classList.add('geo-motion-ready');

    scan(document);
    initHeroes();

    heroEl = document.querySelector('.geo-hero');
    heroMedia = heroEl && heroEl.querySelector('.geo-hero__media');

    setHeaderOffset();
    onScrollFrame(); // set initial header state without waiting for a scroll

    window.addEventListener('scroll', requestScroll, { passive: true });
    window.addEventListener('resize', setHeaderOffset, { passive: true });

    // Catch any content that arrived late (async widgets, fonts reflow, etc.).
    window.addEventListener(
      'load',
      () => {
        scan(document);
        setHeaderOffset();
      },
      { once: true }
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* --------------------------------------------------------------------------
     Theme editor — reveal instantly on section add/reorder so merchants
     always see their content while editing.
     -------------------------------------------------------------------------- */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      scan(event.target);
      event.target
        .querySelectorAll('.geo-reveal')
        .forEach((el) => el.classList.add('is-in'));
      event.target
        .querySelectorAll('.geo-hero')
        .forEach((hero) => hero.classList.add('geo-hero-in'));
      setHeaderOffset();
    });
    document.addEventListener('shopify:section:select', (event) => {
      event.target
        .querySelectorAll('.geo-reveal')
        .forEach((el) => el.classList.add('is-in'));
    });
    document.addEventListener('shopify:section:unload', (event) => {
      event.target.querySelectorAll?.('.geo-reveal').forEach((el) => observer?.unobserve(el));
    });
  }
})();
